import * as THREE from 'three';
import { EffectComposer } from './basler-components.es451.js';
import { UnrealBloomPass } from './basler-components.es452.js';
import { RenderPass } from './basler-components.es453.js';
import { ShaderPass } from './basler-components.es454.js';
import { verticalBlurShader } from './basler-components.es464.js';
import planeFragment from './basler-components.es465.js';
import planeVertex from './basler-components.es466.js';
import { supported } from './basler-components.es460.js';

class CtaCanvasRenderer {
  options;
  scene;
  camera;
  renderer;
  timing = {
    interval: 1 / 10,
    // max 24 FPS
    clock: new THREE.Clock(),
    delta: 0
  };
  webglCanvas = null;
  shaderMaterial = null;
  planeGeometry = null;
  planeMesh = null;
  composer = null;
  paused = true;
  supportsHardwareGL = false;
  /**
   *
   * @param options CtaCanvasRendererOptions
   */
  constructor(options) {
    this.options = options;
    this.webglCanvas = null;
    this.renderer = null;
    this.shaderMaterial = null;
    this.planeGeometry = null;
    this.planeMesh = null;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 200);
    this.paused = false;
    this.supportsHardwareGL = supported({
      failIfMajorPerformanceCaveat: true
    });
  }
  init(webglCanvas) {
    this.webglCanvas = webglCanvas;
    this.renderer = this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: false,
      canvas: this.webglCanvas
    });
    this.renderer.setClearColor(0, 0);
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.camera.aspect = this.webglCanvas.clientWidth / this.webglCanvas.clientHeight;
    this.camera.near = 0.1;
    this.camera.far = 200;
    this.camera.position.z = 170;
    this.camera.position.x = 0;
    this.camera.updateProjectionMatrix();
    try {
      if (/iPad|iPhone|iPod/.test(navigator?.userAgent)) {
      } else {
        this.renderer.setSize(this.webglCanvas.clientWidth, this.webglCanvas.clientHeight, false);
      }
    } catch (e) {
      console.error(e);
    }
    this.initMeshes();
    this.initScene();
  }
  degreesToRadians(degrees) {
    return degrees * (Math.PI / 180);
  }
  setPaused(newStatus) {
    if (this.paused && !newStatus) {
      this.paused = newStatus;
      if (typeof requestAnimationFrame === "undefined") {
        return;
      }
      this.renderLoop();
    }
    this.paused = newStatus;
  }
  initMeshes() {
    const ang_rad = this.camera.fov * Math.PI / 180;
    const fov_y = this.camera.position.z * Math.tan(ang_rad / 2) * 2;
    this.planeGeometry = new THREE.PlaneGeometry(3 * fov_y * this.camera.aspect, fov_y, 256, 256);
    const params = {
      color2: this.options.color2,
      color1: this.options.color1,
      transparency: 0.3,
      progress: 1
    };
    this.shaderMaterial = new THREE.ShaderMaterial({
      // extensions: {
      //   derivatives: '#extension GL_OES_standard_derivatives : enable'
      // },
      uniforms: {
        time: { type: "f", value: 0 },
        colorScheme: { type: "f", value: 2 },
        transparency: { type: "f", value: params.transparency },
        progress: { type: "f", value: params.progress },
        color1: {
          type: "v3",
          value: new THREE.Color(params.color1)
        },
        color2: {
          type: "v3",
          value: new THREE.Color(params.color2)
        }
      },
      vertexShader: planeVertex,
      fragmentShader: planeFragment,
      // wireframe: true,
      side: THREE.DoubleSide,
      transparent: true
    });
    this.planeMesh = new THREE.Mesh(this.planeGeometry, this.shaderMaterial);
    this.planeMesh.position.x = 0;
    this.planeMesh.position.z = 20;
    this.planeMesh.rotateX(this.degreesToRadians(90));
    this.planeMesh.rotateZ(this.degreesToRadians(180));
  }
  initScene() {
    if (!this.renderer) {
      return;
    }
    if (!this.webglCanvas) {
      return;
    }
    if (this.planeMesh) {
      this.scene.add(this.planeMesh);
    }
    if (this.options.showDebugControls) {
      const gridHelper = new THREE.GridHelper(100, 50);
      this.scene.add(gridHelper);
    }
    if (this.options.usePostProcessing) {
      this.composer = new EffectComposer(this.renderer);
      this.renderer.setSize(this.webglCanvas.clientWidth, this.webglCanvas.clientHeight);
      this.composer.addPass(new RenderPass(this.scene, this.camera));
      if (this.options.useUnrealBloom) {
        this.composer.addPass(
          new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.8, 2.5, 0.21)
        );
      } else {
        const pass = new ShaderPass(verticalBlurShader);
        pass.uniforms["v"].value = 1 / this.webglCanvas.clientHeight;
        this.composer.addPass(pass);
      }
    }
  }
  onResize() {
    if (!this.webglCanvas || !this.renderer) {
      return;
    }
    const container = this.webglCanvas.parentElement;
    if (container) {
      const box = container.getBoundingClientRect();
      this.renderer.setSize(box.width, box.height);
      this.camera.aspect = box.width / box.height;
      this.camera.updateProjectionMatrix();
    }
  }
  renderLoop() {
    if (this.paused) {
      return;
    }
    if (this.supportsHardwareGL) {
      requestAnimationFrame(this.renderLoop.bind(this));
    }
    this.timing.delta += this.timing.clock.getDelta();
    if (this.timing.delta > this.timing.interval) {
      if (this.shaderMaterial) {
        this.shaderMaterial.uniforms.time.value += 0.05;
      }
      this.timing.delta = this.timing.delta % this.timing.interval;
      this.renderOnce();
    }
  }
  renderOnce() {
    if (this.renderer) {
      if (this.options.usePostProcessing && this.composer) {
        this.composer.render();
      } else {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }
  dispose() {
    this.paused = true;
    this.renderer?.forceContextLoss();
    this.renderer?.dispose();
    this.planeGeometry?.dispose();
    this.shaderMaterial?.dispose();
    this.composer?.dispose();
    this.webglCanvas = null;
    this.renderer = null;
    this.shaderMaterial = null;
    this.planeGeometry = null;
    this.planeMesh = null;
    this.composer = null;
  }
}

export { CtaCanvasRenderer };
